/**
 * "Mark done" for an onboarding item (plan docs/plans/lms-plan.md 12.3, 12.1,
 * 5.7, 2.3a, G14; WP-47; R14).
 *
 * The evidence the item asks for decides the form: 'text' needs a written
 * confirmation; 'file' needs one PDF, PNG or JPEG of at most 10 MB (checked
 * here as a courtesy; the server checks the type, the size and the bytes);
 * 'none' is a plain confirmation. An external training whose file is offered
 * as a certificate against a linked course (12.1) may carry the certificate's
 * issue and expiry dates. A credential-looking text is refused by the server
 * with { rule, field } and shown in plain words (G14). The same dialog serves
 * the hire's route and the admin's route for admin items; the caller posts.
 */
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { EVIDENCE_MAX_BYTES, EVIDENCE_MIMES, ONBOARDING_LIMITS } from '../../api/onboarding';
import type { OnboardingDoneInput, OnboardingRunItem } from '../../api/onboarding';
import { describeOnboardingError } from './shared';

export interface DoneDialogProps {
  /** The item to mark done; null closes the dialog. */
  item: OnboardingRunItem | null;
  onClose: () => void;
  /** Posts the act; a rejection is shown in the dialog, a success is the caller's to close. */
  onSubmit: (input: OnboardingDoneInput) => Promise<void>;
}

const labelCls = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';
const hintCls = 'mt-1 text-xs text-gray-600 dark:text-gray-400';

/** A courtesy check before the upload (the server's own rules, 2.3a and 12.3). */
export function evidenceFileProblem(file: File | null): string | null {
  if (!file) return 'Choose the file to upload.';
  if (!(EVIDENCE_MIMES as readonly string[]).includes(file.type)) return 'The file must be a PDF, PNG or JPEG.';
  if (file.size > EVIDENCE_MAX_BYTES) return 'The file is larger than 10 MB.';
  return null;
}

export function DoneDialog({ item, onClose, onSubmit }: DoneDialogProps) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    setText('');
    setFile(null);
    setIssuedOn('');
    setExpiresOn('');
    setSaving(false);
    setError(null);
    setTouched(false);
  }, [item?.id]);

  if (!item) return null;

  const evidence = item.evidence;
  const offersCertificate = evidence === 'file' && item.kind === 'external_training' && Boolean(item.course_id);
  const trimmed = text.trim();
  const textProblem =
    evidence === 'text' && !trimmed
      ? 'Write the confirmation (a confirmation number, or a line saying it is done).'
      : trimmed.length > ONBOARDING_LIMITS.evidenceText
        ? `A confirmation is at most ${ONBOARDING_LIMITS.evidenceText} characters.`
        : null;
  const fileProblem = evidence === 'file' ? evidenceFileProblem(file) : null;
  const datesProblem = offersCertificate && issuedOn && expiresOn && expiresOn < issuedOn ? 'The expiry date is before the issue date.' : null;
  const problem = textProblem || fileProblem || datesProblem;

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
    setFile(chosen);
    setTouched(true);
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setTouched(true);
    if (problem || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        evidenceText: evidence === 'text' ? trimmed : null,
        file: evidence === 'file' ? file : null,
        issuedOn: offersCertificate && issuedOn ? issuedOn : null,
        expiresOn: offersCertificate && expiresOn ? expiresOn : null,
      });
    } catch (err) {
      if (alive.current) setError(describeOnboardingError(err, 'The item could not be marked done. Try again.'));
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={saving ? () => {} : onClose} title="Mark done" maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-4" data-testid="onboarding-done-form" noValidate>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</p>

        {evidence === 'text' ? (
          <div>
            <label htmlFor="lms-onboarding-done-text" className={labelCls}>
              Confirmation
            </label>
            <textarea
              id="lms-onboarding-done-text"
              className={`${fieldCls} w-full`}
              rows={3}
              value={text}
              maxLength={ONBOARDING_LIMITS.evidenceText + 50}
              onChange={(e) => {
                setText(e.target.value);
                setTouched(true);
              }}
              data-testid="onboarding-done-text"
            />
            <p className={hintCls}>A confirmation number or a short line saying it is done. Never a password or a login.</p>
          </div>
        ) : null}

        {evidence === 'file' ? (
          <div>
            <label htmlFor="lms-onboarding-done-file" className={labelCls}>
              Evidence file
            </label>
            <input
              id="lms-onboarding-done-file"
              type="file"
              accept={EVIDENCE_MIMES.join(',')}
              onChange={onFile}
              className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50 dark:text-gray-300 dark:file:border-gray-600 dark:file:bg-gray-700 dark:file:text-gray-200"
              data-testid="onboarding-done-file"
            />
            <p className={hintCls}>A PDF, PNG or JPEG of at most 10 MB, such as the certificate or a screenshot.</p>
          </div>
        ) : null}

        {offersCertificate ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <p className="text-xs text-gray-600 dark:text-gray-400 sm:col-span-2">
              The file is also sent to an admin as an external certificate for the linked course. Add its dates if it shows them.
            </p>
            <div>
              <label htmlFor="lms-onboarding-done-issued" className={labelCls}>
                Issued on <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
              </label>
              <input
                id="lms-onboarding-done-issued"
                type="date"
                className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
                value={issuedOn}
                onChange={(e) => setIssuedOn(e.target.value)}
                data-testid="onboarding-done-issued"
              />
            </div>
            <div>
              <label htmlFor="lms-onboarding-done-expires" className={labelCls}>
                Expires on <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
              </label>
              <input
                id="lms-onboarding-done-expires"
                type="date"
                className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
                data-testid="onboarding-done-expires"
              />
            </div>
          </div>
        ) : null}

        {evidence === 'none' ? (
          <p className="text-sm text-gray-700 dark:text-gray-300">Confirm this item is done. It needs no evidence.</p>
        ) : null}

        {touched && problem ? (
          <p className="text-sm text-red-700 dark:text-red-400" data-testid="onboarding-done-problem">
            {problem}
          </p>
        ) : null}

        {error ? (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
            data-testid="onboarding-done-error"
          >
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={saving} data-testid="onboarding-done-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Mark done
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default DoneDialog;
